import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useMerchant } from '@/api/queries';
import { ApiKeysPanel } from '@/features/merchant/api-keys-panel';
import { GatewaysPanel } from '@/features/merchant/gateways-panel';
import { WebhookEndpointsPanel } from '@/features/merchant/webhook-endpoints-panel';
import { Empty, ErrorBox } from '@/components/empty';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export const Route = createFileRoute('/_authed/merchants/$merchantId')({
  component: MerchantDetail,
});

function MerchantDetail() {
  const { t } = useTranslation();
  const { merchantId } = Route.useParams();
  const merchant = useMerchant(merchantId);

  if (merchant.isError) return <ErrorBox message={t('common.error')} />;
  if (merchant.isLoading || !merchant.data) return <Empty title={t('common.loading')} />;

  const m = merchant.data;
  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/merchants">
            <ArrowLeft className="h-4 w-4" /> {t('merchants.detail_back')}
          </Link>
        </Button>
      </div>

      <header className="flex flex-wrap items-baseline justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{m.name}</h1>
          <p className="text-sm text-muted-foreground font-mono">{m.code}</p>
        </div>
      </header>

      <Tabs defaultValue="api-keys">
        <TabsList>
          <TabsTrigger value="api-keys">{t('merchants.api_keys_tab')}</TabsTrigger>
          <TabsTrigger value="gateways">{t('merchants.gateways_tab')}</TabsTrigger>
          <TabsTrigger value="webhooks">{t('merchants.webhooks_tab')}</TabsTrigger>
        </TabsList>

        <TabsContent value="api-keys">
          <ApiKeysPanel merchantId={merchantId} />
        </TabsContent>
        <TabsContent value="gateways">
          <GatewaysPanel merchantId={merchantId} />
        </TabsContent>
        <TabsContent value="webhooks">
          <WebhookEndpointsPanel merchantId={merchantId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
